import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useRef,
  type ReactNode,
} from "react";
import { Navigate } from "react-router-dom";
import {
  AuthError,
  type AuthUser,
  getAccessToken,
  login as authLogin,
  logout as authLogout,
  refreshIfNeeded,
  restoreSession,
} from "@/lib/auth";
import { useIdleTimeout } from "@/hooks/use-idle-timeout";
import { showToast } from "@/lib/toast";
import { SessionTimeoutModal } from "@/components/providers/session-timeout-modal";

interface AuthContextType {
  user: AuthUser | null;
  accessToken: string | null;
  authenticated: boolean;
  loading: boolean;
  // Mejora D (changelog cliente): true si el JWT del realm contiene
  // el rol 'admin'. La gestión de equipos solo aparece para admins.
  isAdmin: boolean;
  // true si el JWT contiene 'platform_admin' (operador de la plataforma).
  // La configuración LLM (proveedores, API keys, modelos por rol, coste)
  // solo existe para este rol; los admins del cliente no la ven.
  isPlatformAdmin: boolean;
  /** Devuelve la AuthError si las credenciales son malas, o null en éxito. */
  login: (username: string, password: string) => Promise<AuthError | null>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  accessToken: null,
  authenticated: false,
  loading: true,
  isAdmin: false,
  isPlatformAdmin: false,
  login: async () => null,
  logout: async () => {},
});

// Cada cuánto comprobamos si el access_token está cerca de caducar.
// 30s es más que suficiente para un token con expires_in típico de 5min.
const REFRESH_POLL_MS = 30_000;

export function AuthProvider({ children }: { children: ReactNode }) {
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState<AuthUser | null>(null);
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const initRef = useRef(false);
  // Ref (no estado) para que el bucle de refresh consulte la inactividad sin
  // recrear el intervalo en cada render.
  const idleShouldRefreshRef = useRef<(() => boolean) | null>(null);

  // Restaurar sesión al montar.
  useEffect(() => {
    if (initRef.current) return;
    initRef.current = true;
    (async () => {
      const restored = await restoreSession();
      if (restored) {
        setUser(restored);
        setAccessToken(getAccessToken());
      }
      setLoading(false);
    })();
  }, []);

  // Polling de refresh. Mejor que `setTimeout(expiresIn)` porque sobrevive
  // a cambios de pestaña pausada y a relojes desincronizados.
  //
  // Se salta si el usuario lleva rato inactivo (ver `use-idle-timeout`): así
  // dejamos de mantener viva la sesión en Keycloak y su idle timeout puede
  // cumplirse, en vez de que la app lo anule renovando cada 30 s.
  useEffect(() => {
    if (!user) return;
    const interval = window.setInterval(async () => {
      if (idleShouldRefreshRef.current && !idleShouldRefreshRef.current()) {
        return;
      }
      const token = await refreshIfNeeded();
      if (!token) {
        // Refresh caducado: sesión muerta.
        setUser(null);
        setAccessToken(null);
        return;
      }
      setAccessToken(token);
    }, REFRESH_POLL_MS);
    return () => window.clearInterval(interval);
  }, [user]);

  const login = useCallback(
    async (username: string, password: string) => {
      try {
        const loggedUser = await authLogin(username, password);
        setUser(loggedUser);
        setAccessToken(getAccessToken());
        return null;
      } catch (err) {
        if (err instanceof AuthError) return err;
        return new AuthError(0, "Error de red al iniciar sesión");
      }
    },
    []
  );

  const logout = useCallback(async () => {
    await authLogout();
    setUser(null);
    setAccessToken(null);
  }, []);

  // Cierre por inactividad. `idle.shouldRefresh()` es lo que corta el bucle de
  // renovación de arriba: sin eso, el poll de 30s mantiene viva la sesión en
  // Keycloak indefinidamente y su idle timeout nunca llega a dispararse.
  const handleIdleTimeout = useCallback(() => {
    void (async () => {
      await authLogout();
      setUser(null);
      setAccessToken(null);
      showToast.info("Sesión cerrada por inactividad");
    })();
  }, []);

  const idle = useIdleTimeout({ enabled: !!user, onTimeout: handleIdleTimeout });
  idleShouldRefreshRef.current = idle.shouldRefresh;

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-surface-100">
        <div className="text-center space-y-3">
          <div className="h-8 w-8 mx-auto animate-spin rounded-full border-4 border-brand border-t-transparent" />
          <p className="text-sm text-content-muted">Conectando…</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return (
    <AuthContext.Provider
      value={{
        user,
        accessToken,
        authenticated: true,
        loading,
        isAdmin: user.roles.includes("admin"),
        isPlatformAdmin: user.roles.includes("platform_admin"),
        login,
        logout,
      }}
    >
      {children}
      <SessionTimeoutModal
        open={idle.showWarning}
        secondsLeft={idle.secondsLeft}
        onStayConnected={() => {
          idle.keepAlive();
          void refreshIfNeeded().then((token) => token && setAccessToken(token));
        }}
        onLogout={() => void logout()}
      />
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
